import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    LinearProgress,
    CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocation } from 'react-router-dom';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing } from 'utils/constants';
import { handleUpdateReportDateRange, convertDate } from 'utils/commonHelpers';
import ReportVisualization from 'components/Reports/ReportVisualization';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import logoImage from '../../assets/images/WSLogo.png';
import { Document, Page, pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

/*
    Generic report component for all database-defined reports.
    Extracts urlname from the route path and fetches report content from /api/reports/content/
*/

const GenericReport = () => {
    const theme = useTheme();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const today = new Date();
    const [startDate, setStartDate] = useState(today.setMonth(today.getMonth() - 1));
    const [endDate, setEndDate] = useState(new Date());
    const [dateRangeValue, setDateRangeValue] = useState('1month');
    const [sectionData, setSectionData] = useState([]);
    const [paragraphData, setParagraphData] = useState([]);
    const [filtersData, setFiltersData] = useState([]);
    const [dateRangeOptions, setDateRangeOptions] = useState([]);
    const [reportInfo, setReportInfo] = useState(null);
    const deviceTime = useRef();
    const deviceType = useRef();
    const powerState = useRef();
    const batteryCharge = useRef();
    const savingsSummary = useRef();
    const [PdfProgress, setPdfProgress] = useState(0);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [numPages, setNumPages] = useState(null);

    // Extract urlname from route path (e.g., /energy-reports/baseline-audit -> baseline-audit)
    const getUrlnameFromPath = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        if (pathParts.length >= 2) {
            return pathParts[pathParts.length - 1]; // Last segment is the urlname
        }
        return null;
    };

    const urlname = getUrlnameFromPath();

    const handleChangeDateRangeValue = (e) => {
        const value = e.target.value;
        setDateRangeValue(value);
        const updatedStartDate = handleUpdateReportDateRange(value);
        setStartDate(updatedStartDate);
    };

    useEffect(() => {
        getDateRangeOptions();
        setNumPages(null);
    }, []);

    // Reset report state when navigating to a different report so the UI cleans up
    // and Submit always runs the currently selected report
    useEffect(() => {
        setSectionData([]);
        setParagraphData([]);
        setFiltersData([]);
        setReportInfo(null);
        setPdfUrl(null);
        setNumPages(null);
        setPdfProgress(0);
    }, [location.pathname]);

    useEffect(() => {
        if (sectionData.length > 0) generatePDF(false);
        // eslint-disable-next-line
    }, [sectionData]);

    const getDateRangeOptions = () => {
        ApiHelper.get('/api/settings/constants/', {}, {}, { values: 'date_rang_options' }).then((res) => {
            setDateRangeOptions(res.data['dateRangeOptions']);
        });
    };

    const DeviceTimeseries = (data) => {
        return [
            {
                name: 'Device vs Time',
                data: data.values
            }
        ];
    };

    const DeviceTimeoptions = (data) => {
        return {
            chart: {
                zoom: {
                    enabled: false
                },
                toolbar: {
                    show: false
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        // Handle click if needed
                    },
                    dataPointMouseEnter: (event) => {
                        event.target.style.cursor = 'pointer';
                    }
                }
            },
            xaxis: {
                categories: data.labels,
                title: {
                    text: 'Date'
                }
            },
            yaxis: {
                title: {
                    text: 'Number of Devices'
                }
            },
            tooltip: {
                intersect: true,
                shared: false
            },
            markers: {
                size: 5
            },
            stroke: {
                curve: 'smooth'
            },
            devicePixelRatio: 4
        };
    };

    const seriesOfDevicesType = (data) => {
        return data.type.values;
    };

    const optionsOfDevicesType = (data) => {
        return {
            chart: {
                toolbar: {
                    show: false
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        // Handle click if needed
                    },
                    dataPointMouseEnter: (event) => {
                        event.target.style.cursor = 'pointer';
                    }
                }
            },
            title: {
                text: 'Devices by Type',
                style: {
                    fontSize: '16px',
                    fontWeight: '500'
                }
            },
            labels: data.type.labels,
            colors: ['#2F3EB1', '#E38B03', '#A303E3', '#0dcc17ff', '#13e0c5ff'],
            legend: {
                position: 'bottom'
            }
        };
    };

    const seriesOfDevicesOS = (data) => {
        return data.os.values;
    };

    const optionsOfDevicesOS = (data) => {
        return {
            chart: {
                toolbar: {
                    show: false
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        // Handle click if needed
                    },
                    dataPointMouseEnter: (event) => {
                        event.target.style.cursor = 'pointer';
                    }
                }
            },
            title: {
                text: 'Devices by OS',
                style: {
                    fontSize: '16px',
                    fontWeight: '500'
                }
            },
            labels: data.os.labels,
            colors: ['#23bbd6ff', '#69e417ff', '#a70448ff', '#6922ecff', '#ec9f0fff'],
            legend: { position: 'bottom' }
        };
    };

    const PowerTimeoptions = (data) => {
        return {
            colors: ['#ffffff', '#036705', '#03F948', '#F4FB04', '#FB041F', '#045DFB'],
            chart: {
                type: 'column',
                height: 400
            },
            title: {
                text: null
            },
            credits: {
                enabled: false
            },
            legend: {
                symbolHeight: 12,
                symbolWidth: 12,
                symbolRadius: 0,
                reversed: true
            },
            xAxis: {
                title: {
                    text: 'Date'
                },
                categories: data.dates
            },
            yAxis: {
                allowDecimals: false,
                min: 0,
                tickInterval: 20,
                title: {
                    text: '% of Day'
                },
                labels: {
                    format: '{value}.0',
                    style: {
                        color: '#000',
                        fontSize: '10px'
                    }
                },
                gridLineWidth: 1,
                gridLineColor: '#e0e0e0'
            },
            tooltip: {
                useHTML: true,
                formatter: function () {
                    return `<div style="margin: -8px;">
                            <div style="font-size: 13px; font-weight: 600; color: #616161; background-color: #eceff1; padding: 5px;">${
                                this.key
                            }</div>
                            <div style="display: flex; align-items: center; justify-content: center; padding-top: 10px; padding-bottom: 10px; margin-left: 10px; margin-right: 10px;">
                                <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${
                                    this.series.color || '#000'
                                }; margin-right: 10px;"></div>
                                <div style="font-size: 13px; color: #616161; display: flex;"><div style="margin-right: 7px;">${
                                    this.series.name
                                }:</div> <div style="font-weight: 600;">${this.y}</div></div>
                            </div>
                        </div>`;
                }
            },
            plotOptions: {
                column: {
                    stacking: 'normal'
                }
            },
            devicePixelRatio: 4,
            series: [
                {
                    name: 'Unknown',
                    data: data.Unknown?.map((value) => parseFloat(value)) || []
                },
                {
                    name: 'All Off',
                    data: data.All_Off?.map((value) => parseFloat(value)) || []
                },
                {
                    name: 'All Sleep',
                    data: data.All_Asleep?.map((value) => parseFloat(value)) || []
                },
                {
                    name: 'CPU On',
                    data: data.CPU_On?.map((value) => parseFloat(value)) || []
                },
                {
                    name: 'All On',
                    data: data.All_On?.map((value) => parseFloat(value)) || []
                },
                {
                    name: 'User Active',
                    data: data.User_Active?.map((value) => parseFloat(value)) || []
                }
            ]
        };
    };

    const Batteryseries = (data) => {
        return [
            {
                name: 'Number of Devices',
                data: data.values
            }
        ];
    };

    const Batteryoptions = (data) => {
        return {
            chart: {
                toolbar: {
                    show: false
                },
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        // Handle click if needed
                    },
                    dataPointMouseEnter: (event) => {
                        event.target.style.cursor = 'pointer';
                    }
                }
            },
            plotOptions: {
                bar: {
                    distributed: true
                }
            },
            xaxis: {
                categories: data.labels,
                title: {
                    text: 'Battery Charge Percentile'
                }
            },
            yaxis: {
                title: {
                    text: 'Number of devices'
                }
            },
            colors: ['#A13426', '#A15D27', '#A19A27', '#26A125', '#25A167', '#259BA1']
        };
    };

    const getReportData = async () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        const reportUrl = pathParts.length >= 2 ? pathParts[pathParts.length - 1] : null;
        if (!reportUrl) {
            toast.error('Invalid report URL');
            return;
        }

        setLoading(true);
        await ApiHelper.post('/api/reports/content/', {
            start_date: convertDate(startDate),
            end_date: convertDate(endDate),
            reporturl: reportUrl
        })
            .then((res) => {
                setSectionData(res.data['sections']);
                setParagraphData(res.data['sectionParagraph']);
                if (res.data['report']) {
                    setReportInfo(res.data['report']);
                }
                setLoading(false);
                toast.success('Successfully done');
                const temp = [
                    { label: 'Start Date', value: convertDate(startDate) },
                    { label: 'End Date', value: convertDate(endDate) }
                ];
                setFiltersData(temp);
            })
            .catch((e) => {
                console.log(e);
                setLoading(false);
                toast.warning(`There is no report data for ${reportUrl}`);
            });
    };

    const addHeader = (doc) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFillColor('#1e4161');
        doc.rect(0, 0, pageWidth, 60, 'F');
        const logoWidth = 60;
        const logoHeight = 60;
        doc.addImage(logoImage, 'PNG', (pageWidth - logoWidth) / 2, 0, logoWidth, logoHeight);
    };

    const generatePDF = async (shouldDownload = false) => {
        setPdfLoading(true);
        setPdfProgress(0);
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'letter',
            lineHeight: 1.2,
            fontSize: 12
        });

        let yPosition = 0;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const headerHeight = 60;
        const footerHeight = 40;
        const margin = 40;
        const maxWidth = pageWidth - margin * 2;
        const maxContentHeight = pageHeight - footerHeight;
        const dpi = window.devicePixelRatio || 1;

        const totalSections = sectionData.length;
        let currentSection = 0;

        addHeader(doc);
        yPosition = headerHeight;
        for (const section of sectionData) {
            if (yPosition > maxContentHeight) {
                doc.addPage();
                yPosition = headerHeight;
            }

            if (section.value !== '{>pagebreak<}') {
                doc.setFontSize(parseInt(section.fontsize));
                doc.setTextColor(section.color || '#000000');
                doc.setFont(undefined, section.style || 'normal');

                const lines = doc.splitTextToSize(section.value.replace('{>pagebreak<}', ''), maxWidth);
                let x;
                if (section.align === 'center') {
                    x = pageWidth / 2;
                } else if (section.align === 'right') {
                    x = pageWidth - margin;
                } else {
                    x = margin;
                }

                doc.text(lines, x, yPosition, { align: section.align || 'left' });
                yPosition += lines.length * section.fontsize;
                if (section.value.includes('{>pagebreak<}')) {
                    doc.addPage();
                    yPosition = headerHeight;
                }
            } else {
                doc.addPage();
                yPosition = headerHeight;
                continue;
            }

            for (const paragraph of section.paragraph) {
                if (yPosition > maxContentHeight) {
                    doc.addPage();
                    yPosition = headerHeight;
                }

                doc.setFontSize(parseInt(paragraph.fontsize));
                doc.setTextColor(paragraph.color || '#000000');
                doc.setFont(undefined, paragraph.style || 'normal');
                const splited_lines = paragraph.body_text.split('{>pagebreak<}');
                splited_lines.forEach((line, i) => {
                    if (i !== 0) {
                        doc.addPage();
                        yPosition = headerHeight;
                    }

                    const paraLines = doc.splitTextToSize(line, maxWidth);
                    let x;
                    if (paragraph.align === 'center') {
                        x = pageWidth / 2;
                    } else if (paragraph.align === 'right') {
                        x = pageWidth - margin;
                    } else {
                        x = margin;
                    }
                    paraLines.forEach((pl) => {
                        doc.text(pl, x, yPosition, { align: paragraph.align || 'left' });
                        yPosition += paragraph.fontsize;
                        if (yPosition > maxContentHeight) {
                            doc.addPage();
                            yPosition = headerHeight;
                        }
                    });
                });
                yPosition += 10;

                // Handle different report types based on report_id or report_data structure
                if (paragraph.report_id === 2) {
                    // Savings Summary
                    const tableData = paragraph.report_data?.rows_data?.map((row) => [row[0], row[1], row[2]]) || [];
                    if (tableData.length > 0) {
                        autoTable(doc, {
                            head: [paragraph.report_data.table_headers],
                            body: tableData,
                            startY: yPosition,
                            margin: { left: margin },
                            styles: { fontSize: 10 }
                        });
                        if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
                            yPosition = doc.lastAutoTable.finalY + 10;
                        } else {
                            yPosition = yPosition + 10;
                        }
                    }
                }

                if (paragraph.report_id === 10) {
                    // Device vs Time
                    const input = document.getElementById('deviceTime');
                    if (input) {
                        const canvas = await html2canvas(input, { scale: dpi });
                        const imgData = canvas.toDataURL('image/png');
                        const imgHeight = canvas.height * (maxWidth / canvas.width);

                        if (yPosition + imgHeight > maxContentHeight) {
                            doc.addPage();
                            yPosition = headerHeight;
                        }

                        doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, canvas.height);
                        yPosition += imgHeight + 10;
                    }
                }

                if (paragraph.report_id === 5) {
                    // Device Type
                    const input = deviceType.current;
                    if (input) {
                        const canvas = await html2canvas(input);
                        const imgData = canvas.toDataURL('image/png');
                        const imgHeight = canvas.height * (maxWidth / canvas.width);

                        if (yPosition + imgHeight > maxContentHeight) {
                            doc.addPage();
                            yPosition = headerHeight;
                        }

                        doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, imgHeight);
                        yPosition += imgHeight + 10;
                    }
                }

                if (paragraph.report_id === 11) {
                    // Power States vs. Time
                    const input = powerState.current;
                    if (input) {
                        const canvas = await html2canvas(input, { scale: dpi });
                        const imgData = canvas.toDataURL('image/png');
                        const imgHeight = canvas.height * (maxWidth / canvas.width);

                        if (yPosition + imgHeight > maxContentHeight) {
                            doc.addPage();
                            yPosition = headerHeight;
                        }

                        doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, imgHeight);
                        yPosition += imgHeight + 10;
                    }
                }

                if (paragraph.report_id === 4) {
                    // Battery Health
                    const input = batteryCharge.current;
                    if (input) {
                        const canvas = await html2canvas(input, { scale: dpi });
                        const imgData = canvas.toDataURL('image/png');
                        const imgHeight = canvas.height * (maxWidth / canvas.width);

                        if (yPosition + imgHeight > maxContentHeight) {
                            doc.addPage();
                            yPosition = headerHeight;
                        }

                        doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, imgHeight);
                        yPosition += imgHeight + 10;
                    }
                }

                // Handle report_data structure (used by OrgHealth)
                if (paragraph.hasOwnProperty('report_data') && paragraph.report_data) {
                    if (paragraph.report_data.hasOwnProperty('row_data')) {
                        const rowData = paragraph.report_data.row_data;

                        if (rowData.title === 'Savings Summary') {
                            const tableData = paragraph.report_data.rows_data?.map((row) => [row[0], row[1], row[2]]) || [];
                            if (tableData.length > 0) {
                                autoTable(doc, {
                                    head: [paragraph.report_data.table_headers],
                                    body: tableData,
                                    startY: yPosition,
                                    margin: { left: margin },
                                    styles: { fontSize: 10 }
                                });
                                if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
                                    yPosition = doc.lastAutoTable.finalY + 10;
                                } else {
                                    yPosition = yPosition + 10;
                                }
                            }
                        }

                        if (rowData.title === 'License Summary' || rowData.title === 'Policy Usage') {
                            const tableData = rowData.rows_data || [];
                            if (tableData.length > 0) {
                                autoTable(doc, {
                                    head: [rowData.table_headers],
                                    body: tableData,
                                    startY: yPosition,
                                    margin: { left: margin },
                                    styles: { fontSize: 10, halign: 'center' },
                                    options: { devicePixelRatio: 'auto' }
                                });
                                if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
                                    yPosition = doc.lastAutoTable.finalY + 10;
                                } else {
                                    yPosition = yPosition + 10;
                                }
                            }
                        }

                        if (rowData.title === 'Devices vs. Time') {
                            const input = document.getElementById('deviceTime');
                            if (input) {
                                const canvas = await html2canvas(input, { scale: 1 });
                                const imgData = canvas.toDataURL('image/png');
                                const imgHeight = canvas.height * (maxWidth / canvas.width);

                                if (yPosition + imgHeight > maxContentHeight) {
                                    doc.addPage();
                                    yPosition = headerHeight;
                                }

                                doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, imgHeight);
                                yPosition += imgHeight + 10;
                            }
                        }

                        if (rowData.title === 'Device Type (%)') {
                            const input = deviceType.current;
                            if (input) {
                                const canvas = await html2canvas(input, { scale: 1 });
                                const imgData = canvas.toDataURL('image/png');
                                const imgHeight = canvas.height * (maxWidth / canvas.width);

                                if (yPosition + imgHeight > maxContentHeight) {
                                    doc.addPage();
                                    yPosition = headerHeight;
                                }

                                doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, imgHeight);
                                yPosition += imgHeight + 10;
                            }
                        }

                        if (rowData.title === 'Power States vs Time') {
                            const input = powerState.current;
                            if (input) {
                                const canvas = await html2canvas(input, { scale: 1 });
                                const imgData = canvas.toDataURL('image/png');
                                const imgHeight = canvas.height * (maxWidth / canvas.width);

                                if (yPosition + imgHeight > maxContentHeight) {
                                    doc.addPage();
                                    yPosition = headerHeight;
                                }

                                doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, imgHeight);
                                yPosition += imgHeight + 10;
                            }
                        }

                        if (rowData.title === 'Battery Health') {
                            const input = batteryCharge.current;
                            if (input) {
                                const canvas = await html2canvas(input);
                                const ctx = canvas.getContext('2d');
                                const dpr = window.devicePixelRatio || 1;
                                canvas.width = canvas.clientWidth * dpr;
                                canvas.height = canvas.clientHeight * dpr;
                                ctx.scale(dpr, dpr);
                                const imgData = canvas.toDataURL('image/png');
                                const imgHeight = canvas.height * (maxWidth / canvas.width);

                                if (yPosition + imgHeight > maxContentHeight) {
                                    doc.addPage();
                                    yPosition = headerHeight;
                                }

                                doc.addImage(imgData, 'PNG', margin, yPosition, maxWidth, imgHeight);
                                yPosition += imgHeight + 10;
                            }
                        }
                    }
                }
            }

            currentSection++;
            setPdfProgress((currentSection / totalSections) * 100);
        }

        if (shouldDownload) {
            const reportName = reportInfo?.name?.toLowerCase().replace(/\s+/g, '_') || urlname || 'report';
            doc.save(`${reportName}_report.pdf`);
        } else {
            const pdfBlob = doc.output('blob');
            const url = URL.createObjectURL(pdfBlob);
            setPdfUrl(url);
        }
        setPdfLoading(false);
        setPdfProgress(0);
    };

    const renderParagraphContent = (paragraph) => {
        // Handle different report types based on report_id
        if (paragraph.report_id === 10) {
            // Device vs Time
            return (
                <div ref={deviceTime} id="deviceTime" style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                    <Chart
                        id="deviceTime"
                        options={DeviceTimeoptions(paragraph.report_data)}
                        type="line"
                        series={DeviceTimeseries(paragraph.report_data)}
                        style={{ width: '90%' }}
                        height={200}
                    />
                    <>
                        <br />
                        <br />
                    </>
                </div>
            );
        }

        if (paragraph.report_id === 5) {
            // Device Type
            return (
                <div ref={deviceType}>
                    <Grid container spacing={gridSpacing} id="deviceType">
                        {loading === true ? (
                            <Grid item xs={12}>
                                <Typography
                                    sx={{
                                        mt: 3,
                                        textAlign: 'center',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Please wait...
                                </Typography>
                            </Grid>
                        ) : (
                            <>
                                <Grid
                                    item
                                    xs={6}
                                    style={{
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        height: '200px',
                                        width: '90%'
                                    }}
                                >
                                    <ReportVisualization
                                        loading={loading}
                                        data={paragraph.report_data.os.values}
                                        type="pie"
                                        options={optionsOfDevicesOS(paragraph.report_data)}
                                        series={seriesOfDevicesOS(paragraph.report_data)}
                                        height="200"
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    style={{
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        height: '200px',
                                        width: '90%'
                                    }}
                                >
                                    <ReportVisualization
                                        loading={loading}
                                        data={paragraph.report_data.type.values}
                                        type="pie"
                                        options={optionsOfDevicesType(paragraph.report_data)}
                                        series={seriesOfDevicesType(paragraph.report_data)}
                                        height="200"
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <>
                        <br />
                        <br />
                    </>
                </div>
            );
        }

        if (paragraph.report_id === 11) {
            // Power States vs. Time
            return (
                <div ref={powerState} style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                    <ReportVisualization
                        loading={loading}
                        data={paragraph.report_data}
                        type="highcharts"
                        highchartsOptions={PowerTimeoptions(paragraph.report_data)}
                        width="90%"
                        height="200"
                    />
                    <>
                        <br />
                        <br />
                    </>
                </div>
            );
        }

        if (paragraph.report_id === 4) {
            // Battery Health
            return (
                <div ref={batteryCharge}>
                    <ReportVisualization
                        loading={loading}
                        data={paragraph.report_data}
                        type="bar"
                        options={Batteryoptions(paragraph.report_data)}
                        series={Batteryseries(paragraph.report_data)}
                        width="90%"
                        height="200"
                    />
                    <>
                        <br />
                        <br />
                    </>
                </div>
            );
        }

        if (paragraph.report_id === 2) {
            // Savings Summary
            return (
                <div ref={savingsSummary}>
                    <ReportVisualization
                        loading={loading}
                        data={paragraph.report_data}
                        type="table"
                        tableHeaders={paragraph.report_data.table_headers}
                        tableData={paragraph.report_data.rows_data.map((data) => [data[0], data[1], data[2]])}
                    />
                    <>
                        <br />
                        <br />
                    </>
                </div>
            );
        }

        // Handle report_data structure (used by OrgHealth and other reports)
        if (paragraph.hasOwnProperty('report_data') && paragraph.report_data) {
            if (paragraph.report_data.hasOwnProperty('row_data')) {
                const rowData = paragraph.report_data.row_data;

                if (rowData.title === 'License Summary' || rowData.title === 'Policy Usage') {
                    return (
                        <div>
                            <ReportVisualization
                                loading={loading}
                                data={paragraph.report_data}
                                type="table"
                                tableHeaders={rowData.table_headers}
                                tableData={rowData.rows_data}
                            />
                            <>
                                <br />
                                <br />
                            </>
                        </div>
                    );
                }

                if (rowData.title === 'Savings Summary') {
                    return (
                        <div>
                            <ReportVisualization
                                loading={loading}
                                data={paragraph.report_data}
                                type="table"
                                tableHeaders={paragraph.report_data.table_headers}
                                tableData={paragraph.report_data.rows_data.map((data) => [data[0], data[1], data[2]])}
                            />
                            <>
                                <br />
                                <br />
                            </>
                        </div>
                    );
                }

                if (rowData.title === 'Devices vs. Time') {
                    return (
                        <div ref={deviceTime} id="deviceTime" style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                            <Chart
                                id="deviceTime"
                                options={DeviceTimeoptions(paragraph.report_data)}
                                type="line"
                                series={DeviceTimeseries(paragraph.report_data)}
                                style={{ width: '90%' }}
                                height={200}
                            />
                            <>
                                <br />
                                <br />
                            </>
                        </div>
                    );
                }

                if (rowData.title === 'Device Type (%)') {
                    return (
                        <div ref={deviceType}>
                            <Grid container spacing={gridSpacing} id="deviceType">
                                {loading === true ? (
                                    <Grid item xs={12}>
                                        <Typography
                                            sx={{
                                                mt: 3,
                                                textAlign: 'center',
                                                fontSize: '16px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Please wait...
                                        </Typography>
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid
                                            item
                                            xs={6}
                                            style={{
                                                textAlign: 'center',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                height: '200px',
                                                width: '90%'
                                            }}
                                        >
                                            <ReportVisualization
                                                loading={loading}
                                                data={paragraph.report_data.os.values}
                                                type="pie"
                                                options={optionsOfDevicesOS(paragraph.report_data)}
                                                series={seriesOfDevicesOS(paragraph.report_data)}
                                                height="200"
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={6}
                                            style={{
                                                textAlign: 'center',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                height: '200px',
                                                width: '90%'
                                            }}
                                        >
                                            <ReportVisualization
                                                loading={loading}
                                                data={paragraph.report_data.type.values}
                                                type="pie"
                                                options={optionsOfDevicesType(paragraph.report_data)}
                                                series={seriesOfDevicesType(paragraph.report_data)}
                                                height="200"
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <>
                                <br />
                                <br />
                            </>
                        </div>
                    );
                }

                if (rowData.title === 'Power States vs Time') {
                    return (
                        <div ref={powerState} style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                            <ReportVisualization
                                loading={loading}
                                data={paragraph.report_data}
                                type="highcharts"
                                highchartsOptions={PowerTimeoptions(paragraph.report_data)}
                                width="90%"
                                height="200"
                            />
                            <>
                                <br />
                                <br />
                            </>
                        </div>
                    );
                }

                if (rowData.title === 'Battery Health') {
                    return (
                        <div ref={batteryCharge}>
                            <ReportVisualization
                                loading={loading}
                                data={paragraph.report_data}
                                type="bar"
                                options={Batteryoptions(paragraph.report_data)}
                                series={Batteryseries(paragraph.report_data)}
                                width="90%"
                                height="200"
                            />
                            <>
                                <br />
                                <br />
                            </>
                        </div>
                    );
                }
            }
        }

        return null;
    };

    // Determine report type from path for display title
    const getReportTypeFromPath = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        if (pathParts.length >= 2) {
            const typePart = pathParts[pathParts.length - 2]; // e.g., "energy-reports"
            return typePart.replace('-reports', '').replace(/\b\w/g, (l) => l.toUpperCase());
        }
        return 'Report';
    };

    const reportType = getReportTypeFromPath();
    const reportTitle = reportInfo?.name || urlname?.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'Report';
    const reportDescription = reportInfo?.description || '';

    return (
        <MainCard title={`${reportType} Reports Management`} cardHeaderTitle={reportTitle} cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={10}>
                    <Typography sx={{ fontSize: '16px' }}>{reportDescription || `Provides a complete ${reportTitle} report.`}</Typography>
                </Grid>
                <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {sectionData.length > 0 && (
                        <>
                            <LoadingButton
                                color="primary"
                                variant="contained"
                                sx={{ width: '60%' }}
                                onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = pdfUrl;
                                    link.download = `${urlname || 'report'}_report.pdf`;
                                    link.click();
                                }}
                                disabled={sectionData.length === 0}
                                loading={pdfLoading}
                            >
                                Download PDF
                            </LoadingButton>
                        </>
                    )}
                </Grid>
                <Grid item xs={3}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={(newValue) => {
                                            setStartDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={(newValue) => {
                                            setEndDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Date Range</InputLabel>
                                    <Select
                                        label="Date Range"
                                        value={dateRangeValue}
                                        name="date_range"
                                        onChange={handleChangeDateRangeValue}
                                    >
                                        {dateRangeOptions.map((data, key) => (
                                            <MenuItem value={data.value} key={key}>
                                                {data.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getReportData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={9}>
                    <Box sx={{ p: 2 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {pdfUrl && (
                                    <div>
                                        <Document
                                            file={pdfUrl}
                                            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                                            loading={<LinearProgress />}
                                        >
                                            {Array.from({ length: numPages }, (_, idx) => (
                                                <div key={idx} style={{ marginBottom: 40, width: '100%' }}>
                                                    <Page pageNumber={idx + 1} renderTextLayer={false} renderAnnotationLayer={false} />
                                                    {idx < numPages - 1 && (
                                                        <div
                                                            style={{
                                                                margin: '20px 0',
                                                                textAlign: 'center'
                                                            }}
                                                        ></div>
                                                    )}
                                                </div>
                                            ))}
                                        </Document>
                                    </div>
                                )}
                            </>
                        )}
                        {sectionData.length > 0 &&
                            sectionData.map((section, index) => (
                                <div
                                    key={index}
                                    style={{
                                        marginLeft: '5vw',
                                        marginRight: '5vw',
                                        position: 'absolute',
                                        left: '-9999px',
                                        width: '600px'
                                    }}
                                >
                                    {section.paragraph.length > 0 &&
                                        section.paragraph.map((paragraph, key) => (
                                            <React.Fragment key={key}>{renderParagraphContent(paragraph)}</React.Fragment>
                                        ))}
                                </div>
                            ))}
                    </Box>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default GenericReport;
